import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { listCrashes, generateEstimate, getEstimate, sendEstimate } from '../../api/adminCrashes';
import { generateEstimatePDF } from '../../utils/generateEstimatePDF';

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

export default function AdminCrashes() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [crashes, setCrashes] = useState([]);
  const [selected, setSelected] = useState(null);   // crash
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [pitch, setPitch] = useState('');
  const [sent, setSent] = useState(false);

  const load = useCallback(async () => { setCrashes(await listCrashes(token)); }, [token]);
  useEffect(() => { load(); }, [load]);

  const open = async (crash) => {
    setSelected(crash); setEstimate(null); setSent(false);
    setEstimate(await getEstimate(crash._id, token));
  };
  const generate = async () => {
    setBusy(true);
    try { setEstimate(await generateEstimate(selected._id, token)); } finally { setBusy(false); }
  };
  const send = async () => {
    if (!email) return;
    await sendEstimate(estimate._id, email, token, pitch); setSent(true);
  };

  return (
    <div className="p-6 space-y-6 text-gray-900">
      <h1 className="text-2xl font-bold text-gray-100">{t('adminCrashes.title')}</h1>

      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm text-gray-800">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="p-3">{t('adminCrashes.date')}</th>
              <th className="p-3">{t('adminCrashes.vehicle')}</th>
              <th className="p-3">{t('adminCrashes.side')}</th>
              <th className="p-3">{t('adminCrashes.severity')}</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {crashes.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gray-500">{t('adminCrashes.empty')}</td></tr>}
            {crashes.map((c) => (
              <tr key={c._id} className="border-t">
                <td className="p-3">{new Date(c.occurredAt).toLocaleString('fr-FR')}{c.simulated && <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">{t('adminCrashes.simulation')}</span>}</td>
                <td className="p-3">{c.vehicle ? `${c.vehicle.plateNumber} — ${c.vehicle.brand || ''} ${c.vehicle.model || ''}` : '—'}</td>
                <td className="p-3">{c.analysis?.sector || '—'}</td>
                <td className="p-3">{c.analysis?.severity || '—'}</td>
                <td className="p-3"><button onClick={() => open(c)} className="text-blue-600">{t('adminCrashes.estimate')}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="rounded-xl border bg-white p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">{t('adminCrashes.reportFor')} {selected.vehicle?.plateNumber}</h2>
            <button onClick={generate} disabled={busy} className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">
              {busy ? t('common.loading') : t('adminCrashes.generate')}
            </button>
          </div>

          {!estimate && <p className="text-sm text-gray-500">{t('adminCrashes.noEstimate')}</p>}
          {estimate && (
            <>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr><th className="p-2">{t('adminCrashes.part')}</th><th className="p-2">Qté</th><th className="p-2">{t('adminCrashes.best')}</th><th className="p-2">{t('adminCrashes.brand')}</th><th className="p-2">OEM</th><th className="p-2">Économie</th></tr>
                </thead>
                <tbody>
                  {estimate.lines.map((l, i) => {
                    const saving = (l.oem && l.chosen && typeof l.oem.price === 'number' && typeof l.chosen.price === 'number')
                      ? l.oem.price - l.chosen.price : null;
                    return (
                    <tr key={i} className="border-t">
                      <td className="p-2">{l.label}</td>
                      <td className="p-2">{l.qty}</td>
                      <td className="p-2">{l.chosen ? `${fmt(l.chosen.price)} XOF` : <span className="text-amber-600">{t('adminCrashes.toCollect')}</span>}</td>
                      <td className="p-2">{l.chosen?.brand || ''}{l.chosen?.type ? ` (${l.chosen.type})` : ''}</td>
                      <td className="p-2">{l.oem ? `${fmt(l.oem.price)} XOF` : '—'}</td>
                      <td className="p-2">{saving > 0 ? <span className="text-green-600">−{fmt(saving)} XOF</span> : '—'}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-3 rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-gray-800">
                <div className="font-semibold text-gray-700 mb-1">Comparatif pour l'assurance</div>
                <div className="flex flex-wrap gap-x-8 gap-y-1">
                  <span>Total pièces d'origine (OEM) : <b>{fmt(estimate.oemBaseline)} XOF</b></span>
                  <span>Total pièces adaptables : <b>{fmt(estimate.aftermarketTotal)} XOF</b></span>
                </div>
                {estimate.savingsAmount > 0 && estimate.savingsPct != null && (
                  <div className="mt-2 text-green-700 font-bold">
                    Économie en choisissant les pièces adaptables : {fmt(estimate.savingsAmount)} XOF ({estimate.savingsPct} %)
                  </div>
                )}
                <div className="mt-2 text-gray-600 border-t border-green-200 pt-1">
                  Total estimé (meilleures offres) : <b>{fmt(estimate.estimateTotal)} XOF</b>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <textarea
                  value={pitch}
                  onChange={(e) => setPitch(e.target.value)}
                  rows={3}
                  placeholder="Message pour l'assureur (optionnel) — ex: Bonjour, veuillez trouver notre estimation pour le sinistre du véhicule ci-dessous. Les pièces adaptables permettent une économie significative…"
                  className="rounded border px-3 py-2 text-sm w-full max-w-2xl"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => generateEstimatePDF(estimate, selected.vehicle || {})} className="rounded border px-3 py-1.5 text-sm">{t('adminCrashes.downloadPdf')}</button>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="emails (séparés par des virgules)" title="Vous pouvez saisir plusieurs emails, séparés par des virgules" className="rounded border px-3 py-1.5 text-sm w-80" />
                  <button onClick={send} disabled={!email} className="rounded bg-green-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">{t('adminCrashes.send')}</button>
                  {sent && <span className="text-sm text-green-600">{t('adminCrashes.sent')}</span>}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
